/**
 * sharingExtension — `IMailEditorInsert.addSharingAttachments(payload)` 의 NgwEditor extension
 * (PR-N5).
 *
 * TinyMCE 평행: `nine_tinymce_desktop.js` L7363~L7366 의 `window.addSharingAttachments` 가
 * `attachmentLinkJson` (JSON 문자열) 을 `window.insertContent` 로 본문에 직접 삽입.
 *
 * 본 NgwEditor extension 은 동일 시맨틱을 ProseMirror leaf atom 노드로 모델링 — `attachments` attr
 * 에 첨부 메타 배열을 보존하고 `toDOM` 이 `ul/li` 구조로 시각 렌더 (사용자 결정 (a)).
 *
 * ### 보안 — XSS
 *
 * `name` / `url` 은 prosemirror toDOM 의 attribute / text 노드 위치로만 흐른다. PM serializer 가
 * 자동으로 escape — innerHTML 직접 주입 표면 없음. `mimeType` 도 attribute 로만 보존.
 */

import type { NgwExtension } from '@ngweditor/react'

interface SharingAttachmentItem {
  name: string
  url: string
  size?: number
  mimeType?: string
}

interface SharingCommandPayload {
  attachments: ReadonlyArray<SharingAttachmentItem>
}

/** raw 값을 SharingAttachmentItem 으로 변환. 잘못된 항목은 필터링. */
function normalizeAttachments(raw: unknown): SharingAttachmentItem[] {
  if (Array.isArray(raw) === false) return []
  return (raw as unknown[])
    .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    .map((item) => {
      const result: SharingAttachmentItem = {
        name: typeof item.name === 'string' ? item.name : '',
        url: typeof item.url === 'string' ? item.url : '',
      }
      if (typeof item.size === 'number' && Number.isFinite(item.size) === true) {
        result.size = item.size
      }
      if (typeof item.mimeType === 'string') {
        result.mimeType = item.mimeType
      }
      return result
    })
}

export const sharingExtension: NgwExtension = {
  name: 'sharing',

  nodes: {
    sharing_attachments: {
      group: 'block',
      atom: true,
      selectable: false,
      attrs: {
        attachments: { default: [] },
      },
      parseDOM: [
        {
          tag: 'div.nine-sharing-attachments',
          getAttrs: (dom) => {
            if (typeof dom === 'string') return false
            const raw = dom.getAttribute('data-attachments')
            if (raw == null || raw.length === 0) {
              return { attachments: [] }
            }
            try {
              const parsed: unknown = JSON.parse(raw)
              return { attachments: normalizeAttachments(parsed) }
            } catch {
              return { attachments: [] }
            }
          },
        },
      ],
      toDOM: (node) => {
        const attachments = normalizeAttachments(node.attrs.attachments)
        const items = attachments.map((att) => {
          const anchor: [string, Record<string, string>, string] = [
            'a',
            { href: att.url, target: '_blank', rel: 'noopener noreferrer' },
            att.name,
          ]
          const liChildren: unknown[] = [anchor]
          if (att.size != null) {
            liChildren.push(['span', { class: 'nine-sharing-size' }, ` (${att.size} bytes)`])
          }
          return ['li', { class: 'nine-sharing-item' }, ...liChildren]
        })
        return [
          'div',
          {
            class: 'nine-sharing-attachments',
            'data-attachments': JSON.stringify(attachments),
            contenteditable: 'false',
          },
          ['ul', { class: 'nine-sharing-list' }, ...items],
        ]
      },
    },
  },

  commands: {
    addSharingAttachments: (state, dispatch, _view, ...args) => {
      if (dispatch == null) return true

      const arg = args[0]
      if (typeof arg !== 'object' || arg === null) return false
      const payload = arg as SharingCommandPayload
      const attachments = normalizeAttachments(payload.attachments)

      const nodeType = state.schema.nodes.sharing_attachments
      if (nodeType == null) return false

      const node = nodeType.create({ attachments })
      const tr = state.tr.replaceSelectionWith(node)
      dispatch(tr.scrollIntoView())
      return true
    },
  },
}
